import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { icalFeedService } from '../services/icalFeedService';
import { ConfigureICalFeedRequest, ICalFeedSourceKey, ReleaseState } from '../types/icalFeed';

interface FeedEventQueryOptions {
  readonly includeDisappeared?: boolean;
  readonly icalFeedConfigurationId?: string;
  readonly sourceKey?: ICalFeedSourceKey;
  readonly eventType?: 'BOOKING' | 'BLOCKED';
  readonly releaseState?: ReleaseState;
  readonly conflict?: boolean;
}

export const ICAL_FEEDS_QUERY_KEY = 'icalFeeds';
export const ICAL_FEED_EVENTS_QUERY_KEY = 'icalFeedEvents';
export const AVAILABILITY_SOURCE_ATTRIBUTION_QUERY_KEY = 'availabilitySourceAttribution';

// Backward-compatible aliases during query-key migration.
export const ICAL_FEED_QUERY_KEY = ICAL_FEEDS_QUERY_KEY;
export const ICAL_EVENTS_QUERY_KEY = ICAL_FEED_EVENTS_QUERY_KEY;

const icalFeedQueryKeys = {
  list: (apartmentId: string) => [ICAL_FEEDS_QUERY_KEY, apartmentId] as const,
  events: (apartmentId: string, options: FeedEventQueryOptions = {}) =>
    [
      ICAL_FEED_EVENTS_QUERY_KEY,
      apartmentId,
      options.icalFeedConfigurationId ?? null,
      options.sourceKey ?? null,
      options.includeDisappeared ?? false,
      options.eventType ?? null,
      options.releaseState ?? null,
      options.conflict ?? null,
    ] as const,
};

const invalidateApartmentICalQueries = async (queryClient: ReturnType<typeof useQueryClient>, apartmentId: string) => {
  await queryClient.invalidateQueries({ queryKey: icalFeedQueryKeys.list(apartmentId) });
  await queryClient.invalidateQueries({ queryKey: [ICAL_FEED_EVENTS_QUERY_KEY, apartmentId] });
  await queryClient.invalidateQueries({ queryKey: ['availabilities', apartmentId] });
  await queryClient.invalidateQueries({
    queryKey: [AVAILABILITY_SOURCE_ATTRIBUTION_QUERY_KEY, apartmentId],
  });
};

/** Fetches all configured iCal feeds for an apartment (currently backend-compatible single-item list). */
export const useICalFeeds = (apartmentId: string) =>
  useQuery({
    queryKey: icalFeedQueryKeys.list(apartmentId),
    queryFn: () => icalFeedService.listFeeds(apartmentId),
    enabled: !!apartmentId,
  });

/** Fetches the configured iCal feed for a single apartment. Returns `null` when unconfigured. */
export const useICalFeed = (apartmentId: string) =>
  useQuery({
    queryKey: icalFeedQueryKeys.list(apartmentId),
    queryFn: () => icalFeedService.listFeeds(apartmentId),
    select: feeds => feeds[0] ?? null,
    enabled: !!apartmentId,
  });

/** Mutation: create or update the iCal feed URL. Invalidates the feed cache on success. */
export const useConfigureICalFeed = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      apartmentId,
      request,
      sourceKey,
      sourceLabel,
      icalFeedConfigurationId,
    }: {
      apartmentId: string;
      request: ConfigureICalFeedRequest;
      sourceKey?: ICalFeedSourceKey;
      sourceLabel?: string;
      icalFeedConfigurationId?: string;
    }) => {
      if (icalFeedConfigurationId) {
        return icalFeedService.updateFeed(apartmentId, icalFeedConfigurationId, {
          ...request,
          sourceKey,
          sourceLabel,
        });
      }
      return icalFeedService.createFeed(apartmentId, {
        ...request,
        sourceKey,
        sourceLabel,
      });
    },
    onSuccess: async (_data, { apartmentId }) => {
      await invalidateApartmentICalQueries(queryClient, apartmentId);
    },
  });
};

/**
 * Mutation: remove the iCal feed configuration.
 * Invalidates both the feed and events caches on success — no URL means no events.
 */
export const useRemoveICalFeed = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: string | { apartmentId: string; icalFeedConfigurationId?: string }) => {
      const apartmentId = typeof params === 'string' ? params : params.apartmentId;
      const icalFeedConfigurationId =
        typeof params === 'string' ? undefined : params.icalFeedConfigurationId;
      return icalFeedService.deleteFeed(apartmentId, icalFeedConfigurationId);
    },
    onSuccess: async (_data, params) => {
      const apartmentId = typeof params === 'string' ? params : params.apartmentId;
      await invalidateApartmentICalQueries(queryClient, apartmentId);
    },
  });
};

/**
 * Fetches imported iCal events for a single apartment.
 * Pass `includeDisappeared=true` to include events no longer in the remote feed.
 * `includeDisappeared` is part of the query key so each view is cached independently.
 */
export const useICalFeedEvents = (apartmentId: string, includeDisappeared = false) =>
  useICalFeedEventsBySource(apartmentId, { includeDisappeared });

export const useICalFeedEventsBySource = (
  apartmentId: string,
  options: FeedEventQueryOptions = {}
) =>
  useQuery({
    queryKey: icalFeedQueryKeys.events(apartmentId, options),
    queryFn: () => icalFeedService.listFeedEvents(apartmentId, options),
    enabled: !!apartmentId,
  });

// Backward-compatible alias during naming migration.
export const useICalEvents = useICalFeedEvents;

/**
 * Mutation: release an imported external block (eventType=BLOCKED) so its dates
 * become available on this platform. Invalidates imported-events and availability
 * caches on success so the calendar and list views refresh immediately.
 */
export const useReleaseICalEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ apartmentId, eventId }: { apartmentId: string; eventId: string }) =>
      icalFeedService.release(apartmentId, eventId),
    onSuccess: async (_data, { apartmentId }) => {
      await invalidateApartmentICalQueries(queryClient, apartmentId);
    },
  });
};

/**
 * Mutation: re-block a previously released imported external block.
 * Rejected (thrown `IcalActionError` with code `PLATFORM_BOOKING_EXISTS`) when an
 * overlapping platform booking prevents re-blocking.
 */
export const useReblockICalEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ apartmentId, eventId }: { apartmentId: string; eventId: string }) =>
      icalFeedService.reblock(apartmentId, eventId),
    onSuccess: async (_data, { apartmentId }) => {
      await invalidateApartmentICalQueries(queryClient, apartmentId);
    },
  });
};

/**
 * Mutation: trigger an immediate manual sync of a single iCal feed ("Sync now"),
 * bypassing the hourly background cycle. Only accepted by the backend while the
 * feed's `status` is `ACTIVE` — rejected with `IcalActionError` (code `FEED_NOT_ACTIVE`)
 * otherwise. Invalidates feed/events/availability caches on success so `lastSyncedAt`
 * and any newly reconciled bookings/conflicts show up immediately.
 */
export const useSyncICalFeedNow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      apartmentId,
      icalFeedConfigurationId,
    }: {
      apartmentId: string;
      icalFeedConfigurationId: string;
    }) => icalFeedService.syncFeedNow(apartmentId, icalFeedConfigurationId),
    onSuccess: async (_data, { apartmentId }) => {
      await invalidateApartmentICalQueries(queryClient, apartmentId);
    },
  });
};
